import { useState } from "react";
import { Send, CheckCircle2, User, Mail, Building2, Phone } from "lucide-react";
import api, { fmtErr } from "@/lib/api";

/**
 * Public contact form used on the /pro landing.
 * Sends a POST to /api/leads which stores the lead and emails info@smileandshine.es
 * plus an auto-reply to the person.
 */
export default function ContactForm({ lang = "es", source = "pro-landing" }) {
  const isES = lang === "es";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!name.trim() || !email.trim()) {
      setErr(isES ? "Nombre y email son obligatorios." : "Name and email are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", {
        name: name.trim(),
        email: email.trim(),
        company: company.trim(),
        phone: phone.trim(),
        message: message.trim(),
        lang,
        source,
        website, // honeypot: real users leave this empty
      });
      setSent(true);
    } catch (ex) {
      setErr(fmtErr(ex));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="sns-card p-8 md:p-10 text-center" data-testid="contact-form-success">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mb-4">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h3 className="sns-title text-2xl mb-2">
          {isES ? "¡Gracias, mensaje enviado!" : "Thanks, message sent!"}
        </h3>
        <p className="text-slate-500 max-w-md mx-auto">
          {isES
            ? "Hemos recibido tu mensaje y te acabamos de enviar una confirmación por email. Te contestaremos en menos de 24 horas laborables."
            : "We've received your message and just sent you a confirmation email. We'll get back to you within 24 business hours."}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="sns-card p-6 md:p-8 space-y-4" data-testid="contact-form">
      <div className="grid md:grid-cols-2 gap-4">
        <Field
          Icon={User}
          label={isES ? "Nombre" : "Name"}
          required
          value={name}
          onChange={setName}
          testId="contact-name"
          autoComplete="name"
        />
        <Field
          Icon={Mail}
          label="Email"
          type="email"
          required
          value={email}
          onChange={setEmail}
          testId="contact-email"
          autoComplete="email"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field
          Icon={Building2}
          label={isES ? "Empresa (opcional)" : "Company (optional)"}
          value={company}
          onChange={setCompany}
          testId="contact-company"
          autoComplete="organization"
        />
        <Field
          Icon={Phone}
          label={isES ? "Teléfono (opcional)" : "Phone (optional)"}
          type="tel"
          value={phone}
          onChange={setPhone}
          testId="contact-phone"
          autoComplete="tel"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2 text-slate-700">
          {isES ? "Cuéntanos brevemente qué necesitas" : "Tell us briefly what you need"}
        </label>
        <textarea
          className="sns-input min-h-[120px] resize-y"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={isES
            ? "Ej: Gestionamos 8 villas en Portals y buscamos digitalizar el control del equipo…"
            : "E.g. We manage 8 villas in Portals and want to digitalise team control…"}
          maxLength={4000}
          data-testid="contact-message"
        />
      </div>

      {/* Honeypot: hidden from real users, bots may fill it */}
      <input
        type="text"
        name="website"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", opacity: 0 }}
      />

      {err && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3" data-testid="contact-error">
          {err}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-2">
        <button
          type="submit"
          disabled={loading}
          className="sns-btn sns-btn-accent px-6 py-3.5 flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="contact-submit"
        >
          <Send size={16} />
          {loading
            ? (isES ? "Enviando..." : "Sending...")
            : (isES ? "Enviar mensaje" : "Send message")}
        </button>
        <p className="text-xs text-slate-400">
          {isES
            ? "Al enviar aceptas que te contactemos por email. No spam, sin compartir tus datos."
            : "By submitting you agree we may contact you by email. No spam, no data sharing."}
        </p>
      </div>
    </form>
  );
}

function Field({ Icon, label, required, type = "text", value, onChange, testId, autoComplete }) {
  return (
    <div>
      <label className="block text-sm font-semibold mb-2 text-slate-700">
        {label}{required && <span className="text-[var(--coral)] ml-1">*</span>}
      </label>
      <div className="relative">
        {Icon && <Icon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />}
        <input
          type={type}
          className={`sns-input ${Icon ? "pl-11" : ""}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          autoComplete={autoComplete}
          data-testid={testId}
        />
      </div>
    </div>
  );
}
